import React, { useEffect, useState } from 'react';
import { motion, useMotionValueEvent, useScroll } from 'framer-motion';
import { X, Menu } from 'lucide-react';
import ThemeToggle from './ThemeToggle';

const scrollToSection = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const SECTION_LINKS = [
  { label: 'Palette', id: 'demo-section' },
  { label: 'How it works', id: 'how-it-works' },
  { label: 'For stores', id: 'stores-section' },
] as const;

const WEB_APP_URL = 'https://app.thedressup.co/';

const Wordmark: React.FC = () => (
  <a
    href="/"
    className="flex min-h-[44px] items-center font-display text-[19px] font-semibold tracking-[-0.05em] text-ink"
    aria-label="DressUp home"
  >
    Dress<span className="text-muted">Up</span>
  </a>
);

const Navigation: React.FC = () => {
  const [condensed, setCondensed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { scrollY } = useScroll();

  // Motion value subscription instead of a scroll listener: no re-render per frame.
  useMotionValueEvent(scrollY, 'change', (latest) => {
    setCondensed(latest > 24);
  });

  // Keep the page behind the full-screen menu from scrolling under it.
  useEffect(() => {
    if (!menuOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [menuOpen]);

  const go = (id: string) => {
    setMenuOpen(false);
    scrollToSection(id);
  };

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 h-16 transition-colors duration-300 ${
          condensed ? 'border-b border-line bg-paper/85 backdrop-blur-md' : 'border-b border-transparent'
        }`}
      >
        <nav className="shell flex h-full items-center justify-between gap-6">
          <Wordmark />

          <div className="hidden items-center gap-8 lg:flex">
            {SECTION_LINKS.map((link) => (
              <button
                key={link.id}
                type="button"
                onClick={() => go(link.id)}
                className="text-[14px] font-medium text-muted transition-colors hover:text-ink"
              >
                {link.label}
              </button>
            ))}
            <a
              href={WEB_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[14px] font-medium text-muted transition-colors hover:text-ink"
            >
              Open the web app
            </a>
            <ThemeToggle />
            <button type="button" onClick={() => go('signup-section')} className="btn btn-primary py-3 text-[14px]">
              Get early access
            </button>
          </div>

          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            className="-mr-2 flex h-11 w-11 items-center justify-center text-ink lg:hidden"
            aria-label="Open menu"
            aria-expanded={menuOpen}
          >
            <Menu strokeWidth={1.5} className="h-6 w-6" />
          </button>
        </nav>
      </header>

      {menuOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[60] bg-paper lg:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Site menu"
        >
          <div className="shell flex h-16 items-center justify-between">
            <Wordmark />
            <button
              type="button"
              onClick={() => setMenuOpen(false)}
              className="-mr-2 flex h-11 w-11 items-center justify-center text-ink"
              aria-label="Close menu"
            >
              <X strokeWidth={1.5} className="h-6 w-6" />
            </button>
          </div>

          <div className="shell mt-6 flex flex-col">
            {SECTION_LINKS.map((link) => (
              <button
                key={link.id}
                type="button"
                onClick={() => go(link.id)}
                className="border-t border-line py-5 text-left font-display text-3xl tracking-[-0.04em] text-ink"
              >
                {link.label}
              </button>
            ))}
            <a
              href={WEB_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="border-y border-line py-5 font-display text-3xl tracking-[-0.04em] text-ink"
            >
              Open the web app
            </a>

            <ThemeToggle expanded className="mt-8" />

            <button
              type="button"
              onClick={() => go('signup-section')}
              className="btn btn-primary mt-3 w-full py-4 text-base"
            >
              Get early access
            </button>
          </div>
        </motion.div>
      )}
    </>
  );
};

export default Navigation;
